// A collaborative BlockSuite editor bound to a channel doc, synced through
// YDocSyncProvider ↔ chatDocTransport. Supports 'page' (rich-text doc) and
// 'edgeless' (whiteboard). Theme mirrors the app; adds a self-hosted link previewer
// and image crop (BlockSuite 0.19 has neither the crop nor a non-cloud previewer).
import React, { useEffect, useRef, useState } from 'react';
import '@toeverything/theme/style.css'; // AFFiNE design tokens (--affine-*) → the polished look
import '@toeverything/theme/fonts.css';
import { effects as registerBlockElements } from '@blocksuite/blocks/effects';
import { effects as registerEditorElements } from '@blocksuite/presets/effects';
import { AffineSchemas, addImages } from '@blocksuite/blocks';
import { DocCollection, Schema } from '@blocksuite/store';
import type { AffineEditorContainer } from '@blocksuite/presets';

import { getToken, API_BASE } from './api';
import { YDocSyncProvider } from './ydoc-sync-provider';
import { chatDocTransport } from './docs-sync';
import { DocInsertBar } from './DocInsertBar';
import { CropModal } from './CropModal';
import { DrawioModal } from './DrawioModal';
import { WikiLinkOverlay } from './WikiLinkOverlay';
import { Icon } from './icons';

export type EditorMode = 'page' | 'edgeless';

// Reliable image dimensions read straight from the bytes (PNG IHDR / SVG width/height/viewBox),
// so a diagram is sized correctly even when BlockSuite's readImageSize (new Image()) reports 0.
async function measureImageDims(blob: Blob): Promise<{ width: number; height: number } | null> {
  try {
    const buf = new Uint8Array(await blob.arrayBuffer());
    // PNG: signature 89 50 4E 47; IHDR width/height at bytes 16..23 (big-endian)
    if (buf[0] === 0x89 && buf[1] === 0x50 && buf[2] === 0x4e && buf[3] === 0x47) {
      const rd = (o: number) => ((buf[o] << 24) | (buf[o + 1] << 16) | (buf[o + 2] << 8) | buf[o + 3]) >>> 0;
      const w = rd(16);
      const h = rd(20);
      if (w > 0 && h > 0) return { width: w, height: h };
    }
    // SVG: read explicit width/height (px), else the viewBox, from the opening <svg> tag
    const text = new TextDecoder().decode(buf.subarray(0, 4096));
    const tag = (text.match(/<svg[^>]*>/i) || [''])[0];
    const wv = parseFloat((tag.match(/\swidth="([\d.]+)/i) || [])[1] || '');
    const hv = parseFloat((tag.match(/\sheight="([\d.]+)/i) || [])[1] || '');
    if (wv > 0 && hv > 0) return { width: Math.round(wv), height: Math.round(hv) };
    const vb = (tag.match(/viewBox="([^"]+)"/i) || [])[1];
    if (vb) {
      const p = vb.split(/[\s,]+/).map(Number);
      if (p[2] > 0 && p[3] > 0) return { width: Math.round(p[2]), height: Math.round(p[3]) };
    }
  } catch {
    /* fall through */
  }
  return null;
}

/** Register BlockSuite custom elements exactly once (survives HMR + remounts). */
function ensureRegistered(): void {
  try { if (!customElements.get('affine-note')) registerBlockElements(); } catch { /* already defined */ }
  try { if (!customElements.get('affine-editor-container')) registerEditorElements(); } catch { /* already defined */ }
}

function newClientId(): string {
  const who = getToken() ? 'u' : 'anon';
  return `${who}-${Math.random().toString(36).slice(2, 8)}`;
}

// AFFiNE's editor themes off <html data-theme> (its ThemeObserver watches it, starts
// Light, reacts only to *mutations*). FxM themes via the `fx-theme-light` class, so we
// mirror the app theme into data-theme and keep it in sync on toggle.
function affineThemeForApp(): 'light' | 'dark' {
  return document.documentElement.classList.contains('fx-theme-light') ? 'light' : 'dark';
}

let _fxThemeSyncInstalled = false;
function setupAffineThemeSync(): void {
  if (_fxThemeSyncInstalled) return;
  _fxThemeSyncInstalled = true;
  const obs = new MutationObserver(() => {
    const mode = affineThemeForApp();
    if (document.documentElement.getAttribute('data-theme') !== mode) {
      document.documentElement.setAttribute('data-theme', mode);
    }
  });
  obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
}

function nudgeAffineTheme(): void {
  const mode = affineThemeForApp();
  requestAnimationFrame(() => {
    const html = document.documentElement;
    html.removeAttribute('data-theme');
    html.setAttribute('data-theme', mode);
  });
}

export function BlockSuiteEditor({ docId, mode, diagramOpen, onDiagramClose, workspaceId }: {
  docId: string;
  mode: EditorMode;
  diagramOpen?: boolean;
  onDiagramClose?: () => void;
  workspaceId?: string;
}): React.ReactElement {
  const hostRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<AffineEditorContainer | null>(null);
  const modeRef = useRef<EditorMode>(mode);
  modeRef.current = mode;
  const [crop, setCrop] = useState<{ src: string; model: any } | null>(null);
  const [editTarget, setEditTarget] = useState<{ model: any; xml: string } | null>(null);
  const [editorReady, setEditorReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let provider: YDocSyncProvider | null = null;
    const host = hostRef.current;

    ensureRegistered();
    const schema = new Schema().register(AffineSchemas);
    const collection = new DocCollection({ schema });
    collection.meta.initialize();
    const doc = collection.createDoc({ id: docId });
    doc.load();

    provider = new YDocSyncProvider(doc.spaceDoc, docId, chatDocTransport(docId, newClientId()));
    provider
      .connect()
      .then(() => {
        if (cancelled) return;
        if (!doc.root) {
          const root = doc.addBlock('affine:page', {});
          doc.addBlock('affine:surface', {}, root); // the whiteboard/edgeless layer
          const note = doc.addBlock('affine:note', {}, root);
          doc.addBlock('affine:paragraph', {}, note);
        }
        const editor = document.createElement('affine-editor-container') as AffineEditorContainer;
        editor.doc = doc;
        editor.mode = modeRef.current;
        editorRef.current = editor;
        host?.replaceChildren(editor);
        setEditorReady(true);
        setupAffineThemeSync();
        nudgeAffineTheme();
        // Point the bookmark link-previewer at our self-hosted endpoint (privacy).
        requestAnimationFrame(() => {
          try {
            const bmSvc = editorRef.current?.std?.getService?.('affine:bookmark') as any;
            bmSvc?.linkPreviewer?.setEndpoint?.(`${API_BASE}/docs/link-preview`);
          } catch {
            /* bookmark service not ready — falls back to the default previewer */
          }
        });
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error('[docs] editor sync failed', e);
        if (host) host.innerHTML = '<div class="fx-empty-small">Could not load this doc.</div>';
      });

    return () => {
      cancelled = true;
      provider?.destroy();
      editorRef.current = null;
      setEditorReady(false);
      host?.replaceChildren();
    };
  }, [docId]);

  // Live Page ⇄ Whiteboard switch — no doc/provider teardown.
  useEffect(() => {
    editorRef.current?.switchEditor(mode);
  }, [mode]);

  // ── Image crop ───────────────────────────────────────────────────────────
  const openCrop = async (): Promise<void> => {
    const editor = editorRef.current as any;
    if (!editor) return;
    const doc = editor.doc;
    const imgs: any[] = [];
    const walk = (b: any) => { if (!b) return; if (b.flavour === 'affine:image') imgs.push(b); (b.children || []).forEach(walk); };
    walk(doc.root);
    if (!imgs.length) { window.alert('Add an image first, then click Crop.'); return; }
    let model = imgs[imgs.length - 1]; // default: the most recent image
    try {
      const sels = editor.std?.selection?.value ?? [];
      for (const s of sels) {
        const id = s.blockId ?? s.elementId ?? s.from?.blockId;
        const m = id ? doc.getBlock?.(id)?.model : null;
        if (m?.flavour === 'affine:image') { model = m; break; } // prefer the selected image
      }
    } catch {
      /* use the most recent image */
    }
    try {
      const blobs = doc.blobSync ?? doc.collection?.blobSync;
      const data = await blobs?.get(model.sourceId);
      const blob = data instanceof Blob ? data : new Blob([data]);
      setCrop({ src: URL.createObjectURL(blob), model });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[docs] could not load image for crop', e);
      window.alert('Could not load that image to crop.');
    }
  };

  const applyCrop = async (model: any, blob: Blob): Promise<void> => {
    const doc = (editorRef.current as any)?.doc;
    if (!doc) return;
    try {
      const blobs = doc.blobSync ?? doc.collection?.blobSync;
      const sourceId = await blobs?.set(blob);
      const bmp = await createImageBitmap(blob);
      doc.updateBlock(model, { sourceId, width: bmp.width, height: bmp.height });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[docs] apply crop failed', e);
      window.alert('Could not apply the crop.');
    }
  };

  // ── Diagram (draw.io) ─────────────────────────────────────────────────────
  // Drop a drawn diagram onto the whiteboard as an image — the same "insert image on the
  // edgeless canvas" path a paste/drop uses. The SVG embeds the editable draw.io XML, so the
  // diagram can be reopened for editing later. Persisted by BlockSuite like any image.
  const insertDiagramImage = async (blob: Blob): Promise<void> => {
    const editor = editorRef.current as any;
    if (!editor) return;
    try {
      const dims = await measureImageDims(blob);
      const file = new File([blob], 'diagram.svg', { type: blob.type || 'image/svg+xml' });
      const std = editor.std ?? editor.host?.std;
      if (!std) throw new Error('editor std unavailable');
      const ids = await addImages(std, [file]); // uploads + places the image on the canvas
      // addImages sizes via readImageSize (new Image()), which can return {0,0} for some SVGs →
      // a degenerate box offset from the drawing. Parse the real size from the bytes and fix the
      // block (keeping its center) when they disagree.
      const id = ids?.[0];
      if (dims && id) {
        const model = editor.doc?.getBlock?.(id)?.model;
        if (model?.xywh) {
          const [x, y, w, h] = JSON.parse(model.xywh);
          if (Math.abs(w - dims.width) > 1 || Math.abs(h - dims.height) > 1) {
            const cx = x + w / 2;
            const cy = y + h / 2;
            editor.doc.updateBlock(model, {
              width: dims.width,
              height: dims.height,
              xywh: `[${cx - dims.width / 2},${cy - dims.height / 2},${dims.width},${dims.height}]`,
            });
          }
        }
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[diagram] insert failed', e);
      window.alert('Could not add the diagram to the whiteboard.');
    }
  };

  // draw.io embeds the diagram's mxfile XML in the exported SVG's `content` attribute; pull it
  // back out so "Edit diagram" can reopen the editor seeded with the original drawing.
  const extractDiagramXml = async (editor: any, model: any): Promise<string | null> => {
    try {
      const doc = editor.doc;
      const blobs = doc.blobSync ?? doc.collection?.blobSync;
      const data = await blobs?.get(model.sourceId);
      if (!data) return null;
      const text = data instanceof Blob ? await data.text() : new TextDecoder().decode(data);
      if (!/<svg/i.test(text)) return null;
      const m = text.match(/<svg[^>]*\scontent="([^"]*)"/i);
      if (!m) return null;
      const decoded = new DOMParser().parseFromString('<!doctype html><body>' + m[1], 'text/html').body.textContent || '';
      return /mxfile|mxGraphModel/.test(decoded) ? decoded : null;
    } catch {
      return null;
    }
  };

  // Find the selected image (fallback: most recent) and, if it's a draw.io diagram, reopen the
  // editor seeded with its XML. On Insert we replace that image instead of adding a new one.
  const openDiagramEdit = async (): Promise<void> => {
    const editor = editorRef.current as any;
    if (!editor) return;
    const doc = editor.doc;
    const imgs: any[] = [];
    const walk = (b: any) => { if (!b) return; if (b.flavour === 'affine:image') imgs.push(b); (b.children || []).forEach(walk); };
    walk(doc.root);
    let model = imgs[imgs.length - 1] || null;
    try {
      const sels = editor.std?.selection?.value ?? [];
      for (const s of sels) {
        const id = s.blockId ?? s.elementId ?? s.from?.blockId;
        const m = id ? doc.getBlock?.(id)?.model : null;
        if (m?.flavour === 'affine:image') { model = m; break; }
      }
    } catch {
      /* use most-recent image */
    }
    if (!model) { window.alert('Select a diagram on the whiteboard to edit it.'); return; }
    const xml = await extractDiagramXml(editor, model);
    if (!xml) { window.alert('That image is not an editable diagram — draw one with the Diagram button.'); return; }
    setEditTarget({ model, xml });
  };

  // Replace an existing diagram image in place (keep its position/center; resize to the new art).
  const replaceDiagramImage = async (model: any, blob: Blob): Promise<void> => {
    const editor = editorRef.current as any;
    const doc = editor?.doc;
    if (!doc) return;
    try {
      const dims = await measureImageDims(blob);
      const blobs = doc.blobSync ?? doc.collection?.blobSync;
      const sourceId = await blobs?.set(new File([blob], 'diagram.svg', { type: blob.type || 'image/svg+xml' }));
      const upd: any = { sourceId };
      if (dims && model.xywh) {
        const [x, y, w, h] = JSON.parse(model.xywh);
        const cx = x + w / 2;
        const cy = y + h / 2;
        upd.width = dims.width;
        upd.height = dims.height;
        upd.xywh = `[${cx - dims.width / 2},${cy - dims.height / 2},${dims.width},${dims.height}]`;
      }
      doc.updateBlock(model, upd);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[diagram] replace failed', e);
      window.alert('Could not update the diagram.');
    }
  };

  return (
    <div className="fx-docs-editor-wrap">
      {mode === 'page' && <DocInsertBar getEditor={() => editorRef.current} onCropImage={openCrop} />}
      <div ref={hostRef} className="fx-docs-editor" />
      {mode === 'edgeless' && (
        <button
          type="button"
          className="fx-docs-crop-fab"
          onMouseDown={(e) => e.preventDefault()}
          onClick={openCrop}
          title="Crop the selected image"
        >
          <Icon name="screen" size={15} /> Crop image
        </button>
      )}
      {mode === 'edgeless' && (
        <button
          type="button"
          className="fx-docs-editdiag-fab"
          onMouseDown={(e) => e.preventDefault()}
          onClick={openDiagramEdit}
          title="Edit the selected diagram in draw.io"
        >
          <Icon name="compose" size={15} /> Edit diagram
        </button>
      )}
      {editorReady && workspaceId && (
        <WikiLinkOverlay
          editorEl={hostRef.current}
          workspaceId={workspaceId}
        />
      )}
      {crop && (
        <CropModal
          src={crop.src}
          onApply={(blob) => applyCrop(crop.model, blob)}
          onClose={() => { URL.revokeObjectURL(crop.src); setCrop(null); }}
        />
      )}
      {(diagramOpen || editTarget) && (
        <DrawioModal
          initialXml={editTarget?.xml || ''}
          dark={affineThemeForApp() === 'dark'}
          onInsert={(blob) => (editTarget ? replaceDiagramImage(editTarget.model, blob) : insertDiagramImage(blob))}
          onClose={() => { setEditTarget(null); onDiagramClose?.(); }}
        />
      )}
    </div>
  );
}
